import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	IconButton, Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const HEAT_COLORS = {
	green:  [
		{ bg: '#1b5e20', color: '#fff' },
		{ bg: '#2e7d32', color: '#fff' },
		{ bg: '#388e3c', color: '#fff' },
		{ bg: '#43a047', color: '#fff' },
		{ bg: '#66bb6a', color: '#fff' },
	],
	orange: [
		{ bg: '#e65100', color: '#fff' },
		{ bg: '#ef6c00', color: '#fff' },
		{ bg: '#f57c00', color: '#fff' },
		{ bg: '#fb8c00', color: '#fff' },
		{ bg: '#ffa726', color: '#000' },
	],
	red: [
		{ bg: '#ffcdd2', color: '#000' },
		{ bg: '#ef9a9a', color: '#000' },
		{ bg: '#e57373', color: '#fff' },
		{ bg: '#ef5350', color: '#fff' },
		{ bg: '#c62828', color: '#fff' },
	],
}

const DEMO_RESULTS = [
	{ website: 'rentalspot.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Fiat 500 or similar', acriss: 'ECMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'FleetMax', payment: 'Prepaid', price: 42.30 },
	{ website: 'pricefly.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Seat Ibiza or similar', acriss: 'ECMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'CarFirst', payment: 'Prepaid', price: 48.75 },
	{ website: 'bookride.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Opel Corsa or similar', acriss: 'EBMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'ParkDrive', payment: 'Pay at desk', price: 55.20 },
	{ website: 'eurodrive.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Toyota Yaris or similar', acriss: 'ECMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'DrivePro', payment: 'Prepaid', price: 61.90 },
	{ website: 'findwheels.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'VW Polo or similar', acriss: 'EDMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'Velocar', payment: 'Pay at desk', price: 68.00 },
	{ website: 'rentalspot.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Ford Focus or similar', acriss: 'CDMR', transmission: 'Manual', category: 'Compact', mileage: 'Unlimited', provider: 'AutoRent', payment: 'Prepaid', price: 78.45 },
	{ website: 'alphacars.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Peugeot 308 or similar', acriss: 'CDMR', transmission: 'Manual', category: 'Compact', mileage: 'Unlimited', provider: 'DrivePro', payment: 'Pay at desk', price: 82.60 },
	{ website: 'pricefly.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Skoda Octavia or similar', acriss: 'IDMR', transmission: 'Manual', category: 'Intermediate', mileage: '1500 km', provider: 'PrimeFleet', payment: 'Prepaid', price: 89.30 },
	{ website: 'eurodrive.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'VW Golf or similar', acriss: 'CDMR', transmission: 'Automatic', category: 'Compact', mileage: 'Unlimited', provider: 'SpeedCar', payment: 'Prepaid', price: 95.10 },
	{ website: 'autolink.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Mercedes A-Class or similar', acriss: 'CDAR', transmission: 'Automatic', category: 'Compact', mileage: 'Unlimited', provider: 'AutoRent', payment: 'Pay at desk', price: 102.80 },
	{ website: 'bookride.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Peugeot 3008 or similar', acriss: 'SFAR', transmission: 'Automatic', category: 'SUV', mileage: 'Unlimited', provider: 'FastWheels', payment: 'Prepaid', price: 118.50 },
	{ website: 'rentalspot.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'BMW X1 or similar', acriss: 'IFAR', transmission: 'Automatic', category: 'Intermediate SUV', mileage: 'Unlimited', provider: 'RoadGo', payment: 'Pay at desk', price: 135.20 },
	{ website: 'findwheels.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Audi Q3 or similar', acriss: 'SFAR', transmission: 'Automatic', category: 'SUV', mileage: 'Unlimited', provider: 'Velocar', payment: 'Pay at desk', price: 152.90 },
	{ website: 'pricefly.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Mercedes C-Class or similar', acriss: 'FDAR', transmission: 'Automatic', category: 'Full-Size', mileage: 'Unlimited', provider: 'SpeedCar', payment: 'Prepaid', price: 178.40 },
	{ website: 'alphacars.demo', flag: 'ES', dor: '15 Mar 2025', lor: 5, car: 'Volvo XC60 or similar', acriss: 'PFAR', transmission: 'Automatic', category: 'Premium SUV', mileage: 'Unlimited', provider: 'DrivePro', payment: 'Pay at desk', price: 215.00 },
]

function getHeatColor(idx, total) {
	const firstThird = Math.floor(total / 3)
	const secondThird = Math.floor((total * 2) / 3)

	let palette, position
	if (idx < firstThird) {
		palette = HEAT_COLORS.green
		position = idx / Math.max(firstThird - 1, 1)
	} else if (idx < secondThird) {
		palette = HEAT_COLORS.orange
		position = (idx - firstThird) / Math.max(secondThird - firstThird - 1, 1)
	} else {
		palette = HEAT_COLORS.red
		position = (idx - secondThird) / Math.max(total - secondThird - 1, 1)
	}

	const colorIdx = Math.min(Math.floor(position * palette.length), palette.length - 1)
	return palette[colorIdx]
}


export default function RealTimeDemoData() {
	const total = DEMO_RESULTS.length

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader title={
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<Typography variant="subtitle1" fontWeight={600}>Real Time Searches:</Typography>
						<Typography variant="subtitle2" sx={{ fontWeight: 'light', color: 'secondary.main' }}>
							2025-03-12 14:32:05
						</Typography>
					</Box>
				} />

				<Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap', textAlign: 'center' } }}>
								<TableCell>Website</TableCell>
								<TableCell>DOR</TableCell>
								<TableCell>LOR</TableCell>
								<TableCell>Car Model</TableCell>
								<TableCell>Acriss</TableCell>
								<TableCell>Transmission</TableCell>
								<TableCell>Category</TableCell>
								<TableCell>Mileage</TableCell>
								<TableCell>Provider</TableCell>
								<TableCell>Payment</TableCell>
								<TableCell>Price</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_RESULTS.map((r, idx) => {
								const heat = getHeatColor(idx, total)
								return <TableRow key={idx} sx={{
									backgroundColor: idx % 2 === 0 ? '#fafafa' : '#f5f5f5',
									'& td': { py: 1, textAlign: 'center', fontSize: '0.75rem' },
								}}>
									<TableCell>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
											<Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{r.website}</Typography>
											<IconButton size="small" sx={{ color: 'colors.blue', p: 0.3 }}>
												<IconSeeker icon="up-right-from-square" sx={{ fontSize: 12 }} />
											</IconButton>
											<IconButton size="small" sx={{ color: 'colors.green', p: 0.3 }}>
												<IconSeeker icon="image" sx={{ fontSize: 12 }} />
											</IconButton>
										</Box>
									</TableCell>
									<TableCell>{r.dor}</TableCell>
									<TableCell>{r.lor}</TableCell>
									<TableCell sx={{ fontWeight: 500 }}>{r.car}</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>{r.acriss}</TableCell>
									<TableCell>{r.transmission}</TableCell>
									<TableCell>{r.category}</TableCell>
									<TableCell>{r.mileage}</TableCell>
									<TableCell>{r.provider}</TableCell>
									<TableCell>{r.payment}</TableCell>
									<TableCell sx={{
										bgcolor: heat.bg,
										color: heat.color,
										fontWeight: 700,
										fontSize: '0.8rem',
									}}>
										{`€${r.price.toFixed(2)}`}
									</TableCell>
								</TableRow>
							})}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>
	</Grid>
}
